import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, Search, Bell, PawPrint, CalendarDays, FileText, Activity, ChevronRight, Building2 } from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuth } from '../../contexts/AuthContext'
import { useNotificationRefresh } from '../../contexts/NotificationContext'
import { mascotaService } from '../../services/mascotaService'
import { citaService } from '../../services/citaService'
import { fichaService } from '../../services/fichaService'
import { suscripcionClinicaService } from '../../services/suscripcionClinicaService'
import LoadingState from '../../components/ui/LoadingState'
import Avatar from '../../components/ui/Avatar'
import { normalizeFotoUrl } from '../../utils/avatarHelpers'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function formatDateShort(value) {
  if (!value) return 'Sin fecha'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Sin fecha'
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
}

function getPetImage(mascota) {
  const source = mascota?.foto || mascota?.avatar_url
  if (source) return normalizeFotoUrl(source)
  return '/placeholder-pet.svg'
}

function getPetAge(fechaNacimiento) {
  if (!fechaNacimiento) return ''
  const birth = new Date(fechaNacimiento)
  if (Number.isNaN(birth.getTime())) return ''
  const now = new Date()
  const years = now.getFullYear() - birth.getFullYear()
  if (years <= 0) return '1y'
  return `${years}y`
}

export default function DashboardMobileExact() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { contadorNoLeidas } = useNotificationRefresh()
  const { isDueno } = usePermissions()
  const [mascotas, setMascotas] = useState([])
  const [citas, setCitas] = useState([])
  const [fichas, setFichas] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeClinic, setActiveClinic] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        const mascotasPromise = isDueno
          ? mascotaService.getMascotasByDueno().catch(() => mascotaService.getAll())
          : mascotaService.getAll()

        const citasPromise = isDueno
          ? citaService.getCitasByDueno().catch(() => citaService.getAll())
          : citaService.getAll()
        const fichasPromise = fichaService.getAll({ per_page: 200 }).catch(() => null)
        const suscripcionesPromise = suscripcionClinicaService.getAll().catch(() => null)
        const [mascotasResponse, citasResponse, fichasResponse, suscripcionesResponse] = await Promise.all([
          mascotasPromise,
          citasPromise,
          fichasPromise,
          suscripcionesPromise
        ])
        setMascotas(extractArray(mascotasResponse))
        setCitas(extractArray(citasResponse))
        setFichas(extractArray(fichasResponse))
        const suscripciones = extractArray(suscripcionesResponse)
        const current =
          suscripciones.find((item) => item.estado === 'ACTIVA') ||
          suscripciones.find((item) => item.estado === 'PENDIENTE_APROBACION') ||
          null
        setActiveClinic(current?.clinica || null)
      } catch (error) {
        console.error('Error loading mobile dashboard data:', error)
        setMascotas([])
        setCitas([])
        setFichas([])
        setActiveClinic(null)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [isDueno])

  const featuredPets = useMemo(() => mascotas.slice(0, 4), [mascotas])
  const featuredAppointments = useMemo(() => citas.slice(0, 2), [citas])
  const stats = useMemo(() => {
    const mascotasActivas = mascotas.filter((item) => {
      const estado = String(item?.estado || 'ACTIVA').toUpperCase()
      return estado !== 'INACTIVA' && estado !== 'FALLECIDA'
    }).length

    const citasPendientes = citas.filter((item) => {
      const estado = String(item?.estado || '').toUpperCase()
      return ['PENDIENTE', 'APROBADA', 'CONFIRMADA', 'EN_PROCESO'].some((allowed) => estado.includes(allowed))
    }).length

    const mascotasConAlerta = mascotas.filter((item) => {
      const condicionesActivas = Number(item?.condiciones_activas_count || item?.condicionesActivasCount || 0)
      return item?.esta_sana === false || condicionesActivas > 0
    }).length

    return {
      mascotasActivas,
      citasPendientes,
      fichasTotal: fichas.length,
      mascotasConAlerta
    }
  }, [mascotas, citas, fichas])

  const nearestAppointment = useMemo(() => {
    const withDate = citas
      .map((item) => {
        const rawDate = item?.fecha || item?.fecha_cita || item?.fechaCita || item?.fecha_cita_propuesta || item?.fechaCitaPropuesta
        const parsedDate = rawDate ? new Date(rawDate) : null
        return { item, parsedDate }
      })
      .filter(({ parsedDate }) => parsedDate && !Number.isNaN(parsedDate.getTime()))
      .sort((a, b) => a.parsedDate - b.parsedDate)

    return withDate[0]?.item || null
  }, [citas])

  const userName = user?.nombre || 'Usuario'

  if (loading) {
    return <LoadingState message="Cargando panel..." />
  }

  return (
    <div className="min-h-full bg-transparent pb-24">
      <div className="bg-white">
        <div className="px-4 pt-2 pb-1">
          <div className="flex items-center gap-2.5">
            <div className="flex-1 bg-[#f4f7fb] border border-[#e8edf4] rounded-[20px] px-3 py-2 flex items-center gap-1.5 text-[#9aa5b1] text-[13px] text-left">
              <Search className="w-3.5 h-3.5" />
              <span>Buscar mascota, cita o ficha...</span>
            </div>
            <button
              onClick={() => navigate('/notificaciones')}
              className="relative h-9 w-9 rounded-full flex items-center justify-center text-[#555]"
              type="button"
              aria-label="Notificaciones"
            >
              <Bell className="w-5 h-5" />
              {contadorNoLeidas > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-[#e05555] text-white text-[9px] font-bold flex items-center justify-center">
                  {contadorNoLeidas > 9 ? '9+' : contadorNoLeidas}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/perfil')}
              className="h-[34px] w-[34px] rounded-full overflow-hidden flex items-center justify-center"
              type="button"
            >
              <Avatar user={user} size="w-[34px] h-[34px] text-sm" showStatus={false} />
            </button>
          </div>
        </div>

        <div className="px-4 pb-2">
          <button
            onClick={() => navigate('/perfil')}
            className="w-full rounded-[18px] bg-gradient-to-r from-[#7a9a52] to-[#5f7d3f] text-white px-3.5 py-3 flex items-center justify-between text-left shadow-sm transition-transform duration-200 active:scale-[0.99]"
            type="button"
          >
            <div>
              <p className="text-[11px] opacity-90">Panel personal</p>
              <p className="text-[13px] font-semibold">{`Hola, ${userName}`}</p>
            </div>
            <span className="text-[11px] bg-white/20 px-2.5 py-1 rounded-full">Ver perfil</span>
          </button>
        </div>

        <div className="px-4 py-1">
          <button
            onClick={() => navigate('/notificaciones')}
            type="button"
            className="w-full rounded-[14px] bg-[#f0f6e8] px-3.5 py-2.5 flex items-center gap-2 text-left transition-all duration-200 hover:bg-[#eaf3dd]"
          >
            <span className="h-2 w-2 rounded-full bg-[#7a9a52] shrink-0" />
            <span className="text-[12px] text-[#4a6e28] leading-snug">
              {contadorNoLeidas > 0 ? `${contadorNoLeidas} notificaciones sin leer — revisa tus alertas` : 'Revisa tus próximas citas aprobadas'}
            </span>
            <span className="text-[#7a9a52] text-sm ml-auto">›</span>
          </button>
        </div>

        <div className="px-4 pb-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => navigate('/')}
              type="button"
              className="rounded-[14px] bg-[#f7f9fc] border border-[#eceff4] px-2.5 py-2.5 text-left transition-all duration-200 hover:border-[#d9e1ec]"
            >
              <span className="inline-flex items-center gap-1 text-[10px] text-[#7a9a52] font-semibold">
                <PawPrint className="w-3 h-3" />
                Mascotas
              </span>
              <p className="text-[18px] leading-none font-bold text-[#111] mt-1">{stats.mascotasActivas}</p>
              <p className="text-[10px] text-[#8a94a3] mt-1">vivas</p>
            </button>
            <button
              onClick={() => navigate('/citas')}
              type="button"
              className="rounded-[14px] bg-[#f7f9fc] border border-[#eceff4] px-2.5 py-2.5 text-left transition-all duration-200 hover:border-[#d9e1ec]"
            >
              <span className="inline-flex items-center gap-1 text-[10px] text-[#5b87d8] font-semibold">
                <CalendarDays className="w-3 h-3" />
                Citas
              </span>
              <p className="text-[18px] leading-none font-bold text-[#111] mt-1">{stats.citasPendientes}</p>
              <p className="text-[10px] text-[#8a94a3] mt-1">pendientes</p>
            </button>
            <button
              onClick={() => navigate('/fichas')}
              type="button"
              className="rounded-[14px] bg-[#f7f9fc] border border-[#eceff4] px-2.5 py-2.5 text-left transition-all duration-200 hover:border-[#d9e1ec]"
            >
              <span className="inline-flex items-center gap-1 text-[10px] text-[#c9883a] font-semibold">
                <FileText className="w-3 h-3" />
                Fichas
              </span>
              <p className="text-[18px] leading-none font-bold text-[#111] mt-1">{stats.fichasTotal}</p>
              <p className="text-[10px] text-[#8a94a3] mt-1">registradas</p>
            </button>
          </div>
        </div>

        <div className="px-4 pb-3">
          <button
            onClick={() => navigate('/citas')}
            type="button"
            className="w-full rounded-[16px] bg-[#f8fbf4] border border-[#dfead2] px-3.5 py-3 text-left transition-all duration-200 hover:border-[#cdddb7]"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] text-[#4a8a28] font-semibold inline-flex items-center gap-1">
                  <Activity className="w-3.5 h-3.5" />
                  Resumen de salud
                </p>
                <p className="text-[12px] text-[#2f3b2f] mt-0.5">
                  {stats.mascotasConAlerta > 0
                    ? `${stats.mascotasConAlerta} mascota(s) con alertas de seguimiento`
                    : 'Todas tus mascotas están al día'}
                </p>
                {nearestAppointment ? (
                  <p className="text-[11px] text-[#6c7a66] mt-1">
                    Próxima cita: {nearestAppointment.mascota?.nombre || 'Mascota'} ·{' '}
                    {formatDateShort(
                      nearestAppointment.fecha ||
                        nearestAppointment.fecha_cita ||
                        nearestAppointment.fechaCita ||
                        nearestAppointment.fecha_cita_propuesta ||
                        nearestAppointment.fechaCitaPropuesta
                    )}
                  </p>
                ) : (
                  <p className="text-[11px] text-[#6c7a66] mt-1">Sin citas agendadas próximamente</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-[#7a9a52]" />
            </div>
          </button>
        </div>

        <div className="px-4 pb-2 flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-[#111]">Mis mascotas</h2>
          <button onClick={() => navigate('/mascotas')} className="text-[12px] text-[#7a9a52]" type="button">Ver todas ›</button>
        </div>

        {featuredPets.length > 0 ? (
          <div className="px-4 pb-3 overflow-x-auto">
            <div className="flex gap-2.5 w-max">
              {featuredPets.map((mascota, index) => {
                const id = mascota.id_mascota || mascota.idMascota
                const bgColor = index % 2 === 0 ? '#d4b896' : '#e8e0d8'
                const pendingAppointments = Number(mascota.citas_pendientes || mascota.citasPendientes || 0)
                const healthy = mascota.esta_sana !== false && Number(mascota.condiciones_activas_count || 0) === 0
                return (
                  <button
                    key={id || mascota.nombre}
                    onClick={() => (id ? navigate(`/mascotas/${id}`, { state: { mascota } }) : navigate('/'))}
                    className="text-left shrink-0 group"
                    type="button"
                  >
                    <div className="relative w-[118px] h-[92px] rounded-[18px] overflow-hidden flex items-center justify-center ring-1 ring-black/5 group-hover:ring-[#7a9a52]/30 transition-all" style={{ background: bgColor }}>
                      <img
                        src={getPetImage(mascota)}
                        alt={mascota.nombre || 'Mascota'}
                        className="w-full h-full object-cover"
                        onError={(event) => {
                          if (!event.currentTarget.src.includes('/placeholder-pet.svg')) {
                            event.currentTarget.src = '/placeholder-pet.svg'
                          }
                        }}
                      />
                      {pendingAppointments > 0 ? (
                        <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#5b87d8] text-white text-[10px] font-bold border-2 border-white flex items-center justify-center">
                          {pendingAppointments > 9 ? '9+' : pendingAppointments}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[13px] font-bold mt-1 text-[#111]">{mascota.nombre || 'Mascota'}</p>
                    <p className="text-[11px] text-[#aaa]">
                      {mascota.raza || mascota.especie || 'Pet'} {getPetAge(mascota.fecha_nacimiento || mascota.fechaNacimiento) ? `· ${getPetAge(mascota.fecha_nacimiento || mascota.fechaNacimiento)}` : ''}{' '}
                      <span className={healthy ? 'text-[#4a8a28]' : 'text-[#e05555]'}>
                        {healthy ? '✓' : '⚠'}
                      </span>
                    </p>
                  </button>
                )
              })}
            </div>
          </div>
        ) : (
          <div className="px-4 pb-4">
            <button
              onClick={() => navigate('/perfil')}
              type="button"
              className="w-full rounded-2xl border border-dashed border-[#d5dec9] bg-[#f7fbf2] py-8 text-center"
            >
              <Heart className="w-7 h-7 mx-auto text-[#7a9a52] mb-2" />
              <p className="text-sm font-semibold text-[#111]">Aún no tienes mascotas</p>
            </button>
          </div>
        )}

        <div className="px-4 pb-2 flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-[#111]">Próximas citas</h3>
          <button onClick={() => navigate('/citas')} className="text-[12px] text-[#7a9a52]" type="button">Ver todas ›</button>
        </div>

        <div className="px-4 pb-4">
          {featuredAppointments.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {featuredAppointments.map((cita, idx) => {
                const classColor = idx % 2 === 0 ? 'bg-[#5b87d8]' : 'bg-[#c9883a]'
                return (
                  <button
                    key={(cita.id_cita || cita.idCita) || idx}
                    onClick={() => navigate('/citas')}
                    className={`rounded-[16px] px-3 py-3 text-left shadow-sm ${classColor}`}
                    type="button"
                  >
                    <p className="text-[12px] font-bold text-white leading-tight truncate">{cita.tipo_cita || cita.tipoCita || 'Consulta'}</p>
                    <p className="text-[11px] text-white/80 mt-0.5">
                      {formatDateShort(cita.fecha || cita.fecha_cita || cita.fechaCita)} · {cita.hora_cita || cita.horaCita || cita.hora_cita_propuesta || cita.horaCitaPropuesta || '--:--'}
                    </p>
                    <p className="text-[11px] text-white/80 mt-0.5 truncate">
                      {(cita.mascota?.nombre || 'Mascota')} · {(cita.veterinario?.usuario?.nombre || cita.veterinario?.nombre || cita.vet || 'Vet')}
                    </p>
                    <span className="mt-2 inline-block text-[9px] font-bold bg-white/25 text-white rounded-[8px] px-2 py-0.5">
                      {cita.estado || 'PENDIENTE'}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            <button onClick={() => navigate('/citas')} type="button" className="w-full rounded-2xl border border-[#e5e5e5] bg-[#f8f9fa] py-4 text-sm text-[#666]">
              Sin citas próximas
            </button>
          )}
        </div>

        <div className="px-4 pb-4">
          <button
            onClick={() => navigate('/suscripciones-clinicas')}
            type="button"
            className="w-full bg-[#7a9a52] rounded-[16px] px-4 py-3 text-left text-white flex items-center justify-between shadow-sm transition-transform duration-200 active:scale-[0.99]"
          >
            <div>
              <p className="text-[10px] opacity-80">Clínica suscrita</p>
              <p className="text-[13px] font-bold">{activeClinic?.nombre || 'Gestionar suscripciones'}</p>
              {activeClinic?.direccion ? (
                <p className="text-[10px] opacity-80">{activeClinic.direccion}</p>
              ) : null}
            </div>
            <span className="text-xl">🏥</span>
          </button>
        </div>
      </div>
    </div>
  )
}
